/**
 * PF v2 file hub — the visual derivative-ready poll.
 * ============================================================================
 *
 * All that remains of the v1 `order/{sessionId}` transport. The six upload/delete
 * builders that used to live here (visual, document and dedicated-PoA mints plus
 * their deletes) were deleted when uploads moved to the productCode-keyed v2
 * session-file endpoints — see `sessionFiles.transport.ts`. Nothing in
 * PF v2 addresses a file by a legacy numeric product id any more.
 *
 * `resolveVisualDerivative` deliberately stays on its v1 endpoint
 * (`POST /order/{sessionId}/visual/{filename}?type={type}`), for two reasons:
 *   - it is **safe** — it keys on session + filename + derivative type and never
 *     involves a product id, so the ISSUE-2944 product-code re-key cannot break it;
 *   - it is **not replaceable** by the v2 surface yet — `GET …/files/{filename}/
 *     download-url` signs the ORIGINAL (`RAW` / `INPUT`) and takes no variant
 *     parameter, while staging displays `RAW_WEB` and the floor-plan logo displays
 *     `RAW_THUMB`. Asking for a v2 variant parameter is FE↔BE ledger #40.
 *
 * It is a faithful port of the narrow 404/503-aware poll + signed-URL fetch-verify
 * from `usePurchaseSessionVisualOperations.downloadVisual` (decision: option-b
 * fidelity). FileAPI's built-in `loadFile`/`retryRequestUntilSuccess` retries on ANY
 * error and does not re-fetch the GCS object, so we drive the derivative-ready poll
 * ourselves and write the result onto the FileAPI entry via `editFile`.
 */

import type { AxiosError } from 'axios'

import API from 'utils/API/API'
import { Endpoint, RoleMimetype } from 'constants/API'
import { VisualFileType } from 'constants/visual'
import type { UrlDTO } from 'models/visuals'

// ─── Derivative poll tuning (ported from downloadVisual) ──────────────────────

const DELAY_TIME = 2000
const ITERATION_COUNT = 30
/** BE returns 404 while the derivative doesn't exist yet, 503 while processing. */
const awaitedErrorCodes = new Set([404, 503])

const waitFor = (millis: number) => new Promise((resolve) => setTimeout(resolve, millis))

const statusOf = (error: unknown) => (error as AxiosError)?.response?.status ?? -1

// ─── Derivative-ready poll (option-b: 1:1 with legacy downloadVisual) ─────────

/**
 * Polls the BE for a generated visual derivative and returns its fetch-verified
 * signed URL, or `null` if it never became available within the retry budget /
 * on a permanent (non-404/503) error.
 *
 * Two phases, mirroring `usePurchaseSessionVisualOperations.downloadVisual`:
 *   1. Poll `POST /order/{sessionId}/visual/{filename}?type={type}` for the
 *      signed URL — 404/503 mean "still generating", so keep retrying.
 *   2. Fetch-verify the signed URL is actually served by GCS (a URL can be
 *      minted before the object is readable) — retry on 404/503. On any other
 *      error the URL is still returned (matches legacy: store result with URL).
 */
export async function resolveVisualDerivative(
  sessionId: string,
  filename: string,
  type: VisualFileType
): Promise<string | null> {
  const url = Endpoint.VISUAL_PURCHASE_SESSION_DOWNLOAD_URL
    .replace('{sessionId}', encodeURIComponent(sessionId))
    .replace('{filename}', encodeURIComponent(filename))
    .replace('{type}', encodeURIComponent(type))

  // Phase 1 — retrieve the signed download URL (404/503 while generating).
  let signedUrl: string | undefined
  for (let i = 0; i <= ITERATION_COUNT; i++) {
    try {
      const response = await API.post<UrlDTO>(url, {}, {
        headers: { Accept: RoleMimetype.CLIENT },
      }, {
        endpoint: Endpoint.VISUAL_PURCHASE_SESSION_DOWNLOAD_URL,
        ignoreErrorFactory: (error: AxiosError) => awaitedErrorCodes.has(error.response?.status ?? -1),
      })
      if (response.status === 503) {
        await waitFor(DELAY_TIME)
        continue
      }
      signedUrl = response.data?.url
      break
    } catch (error) {
      if (awaitedErrorCodes.has(statusOf(error)) && i < ITERATION_COUNT) {
        await waitFor(DELAY_TIME)
        continue
      }
      break
    }
  }

  if (!signedUrl) return null

  // Phase 2 — verify the signed URL is actually fetchable from GCS.
  for (let i = 0; i < ITERATION_COUNT; i++) {
    try {
      await API.get<unknown>(signedUrl, undefined, {
        endpoint: 'PF_V2_VISUAL_SIGNED_URL',
        ignoreBaseUrl: true,
        ignoreAuthorizationHeaders: true,
        ignoreErrorFactory: (error: AxiosError) => awaitedErrorCodes.has(error.response?.status ?? -1),
      })
      return signedUrl
    } catch (error) {
      const axiosErr = error as AxiosError
      // Non-retryable (not 404/503) — return the URL anyway (legacy behavior).
      if (!axiosErr.response || !awaitedErrorCodes.has(axiosErr.response.status)) {
        return signedUrl
      }
      await waitFor(DELAY_TIME)
    }
  }

  // Exhausted retries — return the URL; the browser will surface a broken image
  // rather than blocking room creation indefinitely.
  return signedUrl
}
